import React, { useCallback, useEffect, useMemo } from 'react'
import './styles/notification-ecomm-options.scss'
import { Well } from '@pushly/aqe/lib/components'
import { Select, Skeleton, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import {
    CatalogItemPickStrategy,
    CatalogItemType,
    CombinedItemStrategy,
    getRankingStrategyOptions,
    getSubscriberScopedOptions,
    isCatalogItemPickStrategy,
    isItemRankingStrategy,
} from '@pushly/models/lib/enums/catalogs'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { ActionDispatcher } from '../types'
import { reconcileSelectedCombinedStrategy } from 'src/features/journeys/selectors/catalog-item-pick-strategy'

interface INotificationCatalogItemOptionsProps {
    className?: string
    options: CombinedItemStrategy[]
    defaultSelectedStrategy?: CombinedItemStrategy
    selectedStrategy?: CombinedItemStrategy
    builder: INotificationBuilderState
    dispatchChanges: ActionDispatcher
    loading?: boolean
    disabled?: boolean
    itemType?: CatalogItemType
    triggerType?: TriggerType
}

function arraysShallowEqual<T>(a: T[], b: T[]) {
    return a.length === b.length && a.every((v, i) => v === b[i])
}

const NotificationCatalogItemOptions = (props: INotificationCatalogItemOptionsProps) => {
    const {
        loading,
        builder,
        disabled,
        options: propsOptions,
        defaultSelectedStrategy,
        selectedStrategy: propsSelectedStrategy,
        itemType,
        triggerType,
        dispatchChanges,
    } = props

    const stateOptions = builder.catalogItemPickStrategyOptions
    const stateSelectedStrategy = builder.selectedCatalogItemPickStrategy

    const [localSelectionStrategy, setLocalSelectionStrategy] = React.useState<CombinedItemStrategy | undefined>(() => {
        return defaultSelectedStrategy ?? propsSelectedStrategy ?? stateSelectedStrategy
    })

    const setOptions = useCallback(
        (nextOptions: CombinedItemStrategy[]) => {
            dispatchChanges({
                type: 'patch',
                entity: 'root',
                data: {
                    catalogItemPickStrategyOptions: nextOptions,
                },
            })
        },
        [dispatchChanges],
    )

    const setSelection = useCallback(
        (nextSelection: CombinedItemStrategy) => {
            setLocalSelectionStrategy(nextSelection)
            dispatchChanges({
                type: 'patch',
                entity: 'root',
                data: {
                    selectedCatalogItemPickStrategy: nextSelection,
                },
            })
        },
        [dispatchChanges, setLocalSelectionStrategy],
    )

    // Sync builder options/selection with derived values
    useEffect(() => {
        if (!propsOptions.length && !propsSelectedStrategy) return

        let nextOptions = stateOptions ?? []
        if (propsOptions.length) {
            if (!arraysShallowEqual(stateOptions ?? [], propsOptions)) {
                nextOptions = propsOptions
                setOptions(nextOptions)
            }
        }

        if (propsSelectedStrategy) {
            const nextSelection = reconcileSelectedCombinedStrategy(nextOptions, propsSelectedStrategy)
            if (nextSelection && nextSelection !== localSelectionStrategy) {
                setSelection(nextSelection)
            }
        }
    }, [
        propsOptions,
        stateOptions,
        propsSelectedStrategy,
        stateSelectedStrategy,
        localSelectionStrategy,
        setOptions,
        setSelection,
    ])

    const handleCatalogStrategyChange = useCallback(
        (strategy: CombinedItemStrategy) => {
            const nextSelection = reconcileSelectedCombinedStrategy(stateOptions ?? [], strategy)
            if (nextSelection) {
                setSelection(nextSelection)
            }
        },
        [stateOptions, setSelection],
    )

    // Determine what strategies are available
    const hasContextStrategies = propsOptions.some((opt) => isCatalogItemPickStrategy(opt))
    const hasRankingStrategies = propsOptions.some((opt) => isItemRankingStrategy(opt))

    // Build options based on the strategy type
    const scopedOptions = useMemo(() => {
        // Use catalog item pick strategies (personalized)
        if (hasContextStrategies) {
            const contextStrategies = propsOptions.filter((opt) =>
                isCatalogItemPickStrategy(opt),
            ) as CatalogItemPickStrategy[]

            return getSubscriberScopedOptions(contextStrategies, triggerType)
        }

        // Otherwise use ranking strategies (popular)
        if (hasRankingStrategies) {
            return getRankingStrategyOptions(itemType)
        }

        return []
    }, [propsOptions, itemType, triggerType, hasContextStrategies, hasRankingStrategies])

    // Group options by their group field for rendering
    const optionsByGroup = useMemo(() => {
        const grouped: Record<string, typeof scopedOptions> = {}
        scopedOptions.forEach((opt) => {
            if (!grouped[opt.group]) {
                grouped[opt.group] = []
            }
            grouped[opt.group].push(opt)
        })
        return grouped
    }, [scopedOptions])

    if (!stateOptions || stateOptions.length === 0) {
        return null
    }

    // Check if the selected strategy is valid
    const isValidSelection = scopedOptions.some((opt) => opt.value === propsSelectedStrategy)
    const safeValue = isValidSelection ? propsSelectedStrategy : undefined

    return (
        <Well
            className={getClassNames('notification-ecomm-options-wrapper', {})}
            title={
                <>
                    <span>Item Selection</span>
                    <Tooltip
                        title={
                            <span>
                                Determines how the item that will be included within the notification will be chosen.
                            </span>
                        }
                    >
                        <QuestionCircleOutlined className="info-icon" />
                    </Tooltip>
                </>
            }
            mode="ghost"
            hideFooter={true}
        >
            <Skeleton loading={loading} active={true} avatar={false} title={false}>
                <div className="item-selection-controls">
                    <div className="item-selection-metric">
                        <Select
                            className="ecomm-item-pick-strategy-select"
                            popupClassName="ecomm-item-pick-strategy-dropdown"
                            placeholder="Select how to choose the item"
                            value={safeValue}
                            onChange={handleCatalogStrategyChange}
                            disabled={disabled}
                            style={{ width: '100%' }}
                        >
                            {Object.entries(optionsByGroup).map(([group, groupOpts]) => (
                                <Select.OptGroup key={group} label={group}>
                                    {groupOpts.map((option) => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                            ))}
                        </Select>
                    </div>
                </div>
            </Skeleton>
        </Well>
    )
}

export default NotificationCatalogItemOptions
